/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { isNullOrWhiteSpace, trim } from './string-extensions';

/**
 * Get the text of the element.
 * @param element - HTML element.
 * @returns The content of the element.
 */
export function getText(element?: HTMLElement | Element): string {
    return element ? element.textContent || '' : '';
}

/**
 * Add a css class to an element.
 * @param element - The element to add the css class to.
 * @param cssClass - The css class to add.
 */
export function addClass(element: HTMLElement, cssClass: string): void {
    if (element && !isNullOrWhiteSpace(cssClass) && !hasClass(element, cssClass)) {
        if (element.classList) {
            element.classList.add(cssClass);
        } else {
            element.className = trim(`${element.className} ${cssClass}`);
        }
    }
}

/**
 * Checks for the presence of a css class on an element.
 * @param element - The element to check the css class of.
 * @param cssClass - The css class to check for.
 * @returns True if the element's className has the specified class, otherwise false.
 */
export function hasClass(element: HTMLElement, cssClass: string): boolean {
    if (!element || isNullOrWhiteSpace(cssClass)) {
        return false;
    } else if (element.classList) {
        return element.classList.contains(cssClass);
    }
    return `' ' ${element.className} ' '`.includes(`' ' ${trim(cssClass)}  ' '`);
}

/**
 * Convert node list to array.
 * @param nodeList - Node List.
 * @returns Array of the node list.
 */
export function nodeListToArray<T extends Node>(
    nodeList: NodeListOf<T> | NodeListOf<Element> | HTMLCollection | NodeList | NamedNodeMap
): T[] {
    if (!nodeList) {
        return [];
    }

    const elements: T[] = [];

    for (let n = 0; n < nodeList.length; n++) {
        elements.push(<T>nodeList[n]);
    }

    return elements;
}

/**
 * Select elements in a context.
 * @param  selector - Can be class, id or tag selector.
 * @param  [context] - Any element to scope the selection. It's optional. If not provided document will be assumed.
 * @returns HTMLElement[].
 */
export function selectElements(selector: string, context?: HTMLElement): HTMLElement[] {
    return selectElementsT<HTMLElement>(selector, context);
}

/**
 * Select elements in a context.
 * @param  selector - Can be class, id or tag selector.
 * @param  [context] - Any element to scope the selection. It's optional. If not provided document will be assumed.
 * @returns T[].
 */
export function selectElementsT<T extends HTMLElement>(selector: string, context?: HTMLElement): T[] {
    if (isNullOrWhiteSpace(selector) || selector === '#') {
        return <T[]>[];
    }

    const currentContext: HTMLElement | Document = context || document;

    // Check if the first character is class or id.
    if (/^[#.]?[\w-]+$/.test(selector)) {
        // eslint-disable-next-line default-case
        switch (selector[0]) {
            case '.':
                if (currentContext.getElementsByClassName) {
                    return nodeListToArray<T>(currentContext.getElementsByClassName(selector.slice(1)));
                }
                return nodeListToArray<T>(currentContext.querySelectorAll(selector));

            case '#':
                const element = currentContext.querySelector(selector);
                return <T[]>(element ? [element] : []);
        }

        return nodeListToArray<T>(currentContext.getElementsByTagName(selector));
    }

    return nodeListToArray<T>(currentContext.querySelectorAll(selector));
}

/**
 * Sets or gets CSS properties.
 * @param  element - HTML Element.
 * @param  property - The CSS property name.
 * @param  [value] - The value to set on the CSS property.
 * @returns - The value of the CSS property.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function css(element: HTMLElement | Node, property: string, value?: any): any {
    if (!element) {
        return null;
    }

    if (value || value === '') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (<any>element).style[property] = value;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value = (<any>element).style[property];

        if (isNullOrWhiteSpace(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value = getComputedStyle(element as any);
            value = value[property];
        }

        return value;
    }
}

/**
 * Remove all inner HTML from a parent element - used to delete child elements.
 * @param parentElement - The parent element from which all children should be removed.
 */
export function removeInnerHtml(parentElement: HTMLElement): void {
    if (parentElement) {
        parentElement.innerHTML = '';
    }
}

/**
 * Select first element from the selected elements using provided selector.
 * @param selector - Element selector.
 * @param [context] - An optional context to scope the selection.
 * @returns  - The first element from the match.
 */
export function selectFirstElement(selector: string, context?: HTMLElement): HTMLElement | null {
    const elementsSelected = selectElementsT<HTMLElement>(selector, context);
    return !elementsSelected || !elementsSelected.length ? null : elementsSelected[0];
}
